import React from "react";
import { css } from "@emotion/css";
import {
  PageConfig,
  useMultiPageLessonStore,
  useTotalPages,
  useUserActionPayload,
} from "../../../store/multiPageLessonStore";
import { useSprintWebSocket } from "../../../context/SprintWebSocketContext";

// Navigation button styles [except for the feedback modal, that's its own component, including that modal's navigation buttons]
const navigationContainerStyle = css`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const buttonBaseStyle = css`
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 85%;
    max-width: 360px;
  }
`;

const previousButtonStyle = css`
  ${buttonBaseStyle}
  background-color: #2d2d3a;
  color: #e0e0e0;
  border: 1px solid #3d3d4d;

  &:hover {
    background-color: #3d3d4d;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const nextButtonStyle = css`
  ${buttonBaseStyle}
  background-color: #4f46e5;
  color: white;
  border: none;
  width: 100%;

  &:hover {
    background-color: #4338ca;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #3d3d4d;
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const nextButtonContainerStyle = css`
  flex: 1;

  @media (max-width: 768px) {
    width: 85%;
    max-width: 360px;
  }
`;

const completeButtonStyle = css`
  ${buttonBaseStyle}
  background-color: #0c5a4d;
  color: white;
  border: none;
  width: 100%;

  &:hover {
    background-color: #059669;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #3d3d4d;
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
interface NextPageButtonProps {
  pageConfig: PageConfig;
  currentPageIndex: number;
  totalPages: number;
  onNextPage: () => void;
  onShowFeedback: (namespace: string) => void;
}

const NextPageButton: React.FC<NextPageButtonProps> = ({
  pageConfig,
  currentPageIndex,
  totalPages,
  onNextPage,
  onShowFeedback,
}) => {
  const isLastPage = currentPageIndex >= totalPages - 1;

  // Use a different style for the complete button vs next page button

  return (
    <>
      <div>
        {pageConfig?.pageType === "read_only_content" ? (
          <ReadOnlyPageButton isLastPage={isLastPage} onNextPage={onNextPage} />
        ) : (
          <OneActivityPageButton
            isLastPage={isLastPage}
            onNextPage={onNextPage}
            onShowFeedback={onShowFeedback}
          />
        )}
      </div>
    </>
  );
};

const OneActivityPageButton = ({
  isLastPage,
  onNextPage,
  onShowFeedback,
}: {
  isLastPage: boolean;
  onNextPage: () => void;
  onShowFeedback: (namespace: string) => void;
}) => {
  const userActionPayload = useUserActionPayload();
  const { sendJsonMessage } = useSprintWebSocket();

  const handleCheck = () => {
    // Process and send all stored user actions via WebSocket
    if (userActionPayload.size > 0) {
      // Track which blocks need to show feedback
      const feedbackToShow = [];

      // Send each stored action as a separate WebSocket message
      userActionPayload.forEach((payload, blockKey) => {
        console.log(`Sending action for block ${blockKey}:`, payload);
        sendJsonMessage(payload);

        // Collect feedback info if available
        if (payload.showFeedback && payload.feedbackNamespace) {
          feedbackToShow.push(payload.feedbackNamespace);
        }
      });

      // Show feedback immediately and let the modal handle loading state
      if (feedbackToShow.length > 0) {
        // Show feedback for each block that needs it immediately
        feedbackToShow.forEach((namespace) => {
          onShowFeedback(namespace);
        });
      }
    } else {
      // If no actions stored, just proceed to next page or complete lesson
      // TODO: Show oops / error page and capture to sentry
      //   if (!isLastPage) {
      //     onNextPage();
      //   } else {
      //     console.log("Lesson completed!");
      //   }
    }
  };

  // Check if there are any user actions to submit
  const hasUserActions = userActionPayload.size > 0;

  return (
    <>
      <button
        onClick={handleCheck}
        className={isLastPage ? completeButtonStyle : nextButtonStyle}
        disabled={!hasUserActions}
      >
        Check
      </button>
    </>
  );
};

const ReadOnlyPageButton = ({
  isLastPage,
  onNextPage,
}: {
  isLastPage: boolean;
  onNextPage: () => void;
}) => {
  return (
    <>
      {isLastPage ? (
        <button
          className={completeButtonStyle}
          onClick={() => {
            // TODO: Handle lesson completion
            console.log("Lesson completed!");
          }}
        >
          Complete Lesson
        </button>
      ) : (
        <button onClick={onNextPage} className={nextButtonStyle}>
          Next
        </button>
      )}
    </>
  );
};

// Export at the top level
// Main navigation component
export const LessonNavigationButtons = ({
  pageConfig,
  currentPageIndex,
  onShowFeedback,
}: {
  pageConfig: PageConfig;
  currentPageIndex: number;
  onShowFeedback: (namespace: string) => void;
}) => {
  const pageIndex = currentPageIndex;
  const totalPages = useTotalPages();
  const { setCurrentPage } = useMultiPageLessonStore();

  const handleNextPage = () => {
    if (pageIndex < totalPages - 1) {
      setCurrentPage(pageIndex + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageIndex > 0) {
      setCurrentPage(pageIndex - 1);
    }
  };

  return (
    <div className={navigationContainerStyle}>
      {pageIndex > 0 && (
        <button onClick={handlePreviousPage} className={previousButtonStyle}>
          Previous Page
        </button>
      )}

      <div className={nextButtonContainerStyle}>
        <NextPageButton
          pageConfig={pageConfig}
          currentPageIndex={pageIndex}
          totalPages={totalPages}
          onNextPage={handleNextPage}
          onShowFeedback={onShowFeedback}
        />
      </div>
    </div>
  );
};
